import { isAnyOf } from "@reduxjs/toolkit";

import { Conversation } from "../../pages/Apollo/schema/get-people";
import { ListPerson, ListPersonSchedule } from "../../pages/List/schema/list";
import { scheduleApi } from "../schedule/schedule.api";

/**
 * The columns a schedule mutation hands back that the inbox row has to mirror. Every write to one
 * schedule — cancel, pause, resume, edit, send now — answers with the row's new timestamps and `active`
 * flag, and the conversation bubble re-renders from the slice, not from a refetch.
 *
 * An edit (PREC-4220) also answers with the draft `conversation` it rewrote: the bubble's body and
 * subject come from `lead.prospect.conversations[]`, not from the schedule row, so that row is
 * mirrored too when the payload carries it.
 */
export type InboxSchedulePatch = Pick<
	ListPersonSchedule,
	"id" | "listPersonId" | "active" | "canceledAt" | "processedAt" | "publishedAt" | "scheduledFor"
> &
	Partial<
		Pick<ListPersonSchedule, "lastActivity" | "gatedByScheduleId" | "scheduledInDays"> & {
			conversation: Pick<
				Conversation,
				"id" | "message" | "subject" | "scheduledFor" | "sentAt"
			> | null;
		}
	>;

/** Every write to one scheduled message the inbox mirrors through {@link patchInboxSchedule}. */
export const inboxSchedulePatchMatcher = isAnyOf(
	scheduleApi.endpoints.cancelSchedule.matchFulfilled,
	scheduleApi.endpoints.pauseSchedule.matchFulfilled,
	scheduleApi.endpoints.resumeSchedule.matchFulfilled,
	scheduleApi.endpoints.editScheduledMessage.matchFulfilled,
	scheduleApi.endpoints.sendScheduleNow.matchFulfilled
);

const findInboxPerson = (inbox: ListPerson[], listPersonId: number): ListPerson | undefined => {
	const person = inbox.find((candidate) => candidate.id === listPersonId);

	return person?.prospect ? person : undefined;
};

const findInboxSchedule = (
	inbox: ListPerson[],
	{ id, listPersonId }: Pick<ListPersonSchedule, "id" | "listPersonId">
): ListPersonSchedule | undefined =>
	findInboxPerson(inbox, listPersonId)?.schedules?.find((schedule) => schedule.id === id);

/** The draft bubble an edited schedule belongs to, if the inbox row holds it. */
const findInboxConversation = (
	inbox: ListPerson[],
	listPersonId: number,
	conversationId: number
): Conversation | undefined =>
	findInboxPerson(inbox, listPersonId)?.prospect?.conversations?.find(
		(conversation) => conversation.id === conversationId
	);

/**
 * Mirror one schedule's new state into the inbox row that holds it (cancel / pause / resume /
 * edit). A payload for a lead or a schedule the inbox does not hold is ignored.
 */
export const patchInboxSchedule = (inbox: ListPerson[], payload: InboxSchedulePatch): void => {
	const schedule = findInboxSchedule(inbox, payload);

	if (!schedule) {
		return;
	}

	schedule.canceledAt = payload.canceledAt;
	schedule.processedAt = payload.processedAt;
	schedule.publishedAt = payload.publishedAt;
	schedule.scheduledFor = payload.scheduledFor;
	schedule.active = payload.active;

	if (payload.lastActivity !== undefined) {
		schedule.lastActivity = payload.lastActivity;
	}

	if (payload.gatedByScheduleId !== undefined) {
		schedule.gatedByScheduleId = payload.gatedByScheduleId;
	}

	if (payload.scheduledInDays !== undefined) {
		schedule.scheduledInDays = payload.scheduledInDays;
	}

	if (payload.conversation) {
		patchInboxConversation(inbox, payload.listPersonId, payload.conversation);
	}
};

const patchInboxConversation = (
	inbox: ListPerson[],
	listPersonId: number,
	draft: NonNullable<InboxSchedulePatch["conversation"]>
): void => {
	const conversation = findInboxConversation(inbox, listPersonId, draft.id);

	if (!conversation) {
		return;
	}

	conversation.message = draft.message;
	conversation.subject = draft.subject;
	conversation.scheduledFor = draft.scheduledFor;
	conversation.sentAt = draft.sentAt;
};

/**
 * The legacy `schedule/toggle/:id` answer: the API flipped `active` on every row of the lead, so
 * the inbox flips every row too — but only when the row it named actually changed direction.
 */
export const toggleInboxSchedules = (
	inbox: ListPerson[],
	payload: Pick<ListPersonSchedule, "id" | "listPersonId" | "active">
): void => {
	const schedule = findInboxSchedule(inbox, payload);

	if (!schedule || schedule.active === undefined || schedule.active === payload.active) {
		return;
	}

	inbox
		.find((candidate) => candidate.id === payload.listPersonId)
		?.schedules?.forEach((row) => {
			row.active = payload.active;
		});
};
